import {canStartTask, TASK_ASSIGN_BEFORE_START} from './taskTimerState';
import React, {
    ChangeEvent,
    ChangeEventHandler,
    CSSProperties,
    FC,
    KeyboardEventHandler,
    MouseEventHandler, SyntheticEvent,
    TouchEventHandler,
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import './TaskComponent.less';
import util from "../lib/util";
import Task, {ImpactType, UrgencyType} from "../entities/task";
import Moment from "moment";
import IconButton from "../ui/IconButton/IconButton";
import Icons from "../ui/IconButton/Icons";
import ContextMenu from "../ui/ContextMenu/ContextMenu";
import DivMenu from "../ui/ContextMenu/DivMenu";
import CaseHelper from "../Case/CaseHelper";
import TextField from "../ui/TextField";
import FinancialHelper from "../Financial/FinancialHelper";
import NotificationFlag from '../ui/Notification/NotificationFlag';
import LeadHelper from '../CRM/Sales/LeadHelper';
import StateLabel from '../ui/StateLabel/StateLabel';
import * as TaskHelper from './TaskHelper';
import {AWAITING_TITLE, getTaskBackgroundColor} from './TaskHelper';
import {startTimer, stopTimer} from './timers';
import * as DateUtil from '../lib/dateutils';
import i18n from '../i18n/i18n';
import {dialogNoButtons} from '../ui/Confirm';
import {InView} from 'react-intersection-observer';
import {formatUniqueNameOverride, formatUserName, replaceNames} from "../Contacts/ContactFormat";
import find from "../store/find";
import {getCurrentUserId} from "../lib/picos/currentLogin";
import {isMobileView, isTouchDevice} from "../lib/picos/browserFns";
import {formatDuration} from "../lib/picos/duration";
import newHRTimestamp from "../lib/picos/timestamp";
import {connect} from "react-redux";
import {deletedFilter} from "../lib/picos/filters";
import {iconErrorRender} from "../ErrorBoundary/ErrorBoundaryO";
import OptionHelper from "../Configuration/GeneralOptions/OptionHelper";
import EmojiBar from "../ui/EmojiBar/EmojiBar";
import {ContextMenuItemProps} from "../ui/ContextMenu/ContextMenuItem";
import {VoidCallback} from "../@types/functions";
import type {RootState} from "../@types/store";
import Contact from "../entities/contact";
import {EntityType} from "../@types/entity";
import Group from "../entities/group";
import User from "../entities/user";
import Matter from "../entities/matter";

/**
 * Only use handler if test is true.
 *
 * @param {boolean} test
 * @param {function} handler
 * @returns {any}
 */
function wrapHandler(test: boolean, handler: Function): any {
    return test ? handler : undefined;
}

// Exported unconnected for unit testing (the default export below is the
// Redux-connected component). mapStateToProps only injects contacts/documents,
// so tests render this directly with those two passed as props.
export const TaskComponent: FC<TaskComponentProps> = (props) => {
    const {showEmojiBar = true, showOwner = true, showRelatedTo = true, contextMenuItems = []} = props;

    const [showActions, setShowActions] = useState(false);
    const [taskTime, setTaskTime] = useState(0);
    const [isRunning, setIsRunning] = useState(props.task.running);
    const [completionDate, setCompletionDate] = useState(props.task.completionDate);
    const [asOf, setAsOf] = useState(null);

    // When the timer is running, we set an interval every second to update the display.
    let runningIntervalRef = useRef<NodeJS.Timeout>(null);

    const runningCallback = useCallback(() => {
        const _taskTime = props.task.elapsedTime + Math.max(0, Date.now() - props.task.lastStart);
        // Functional update so this callback does NOT depend on taskTime. If it
        // did, its identity would change on every tick, re-running the effect
        // below and tearing down/recreating the interval once per second.
        setTaskTime(prev => (prev !== _taskTime ? _taskTime : prev));
    }, [props.task.elapsedTime, props.task.lastStart]);

    /**
     * Called from the browser when the tab is losing visibility
     */
    const prepareForFreeze = useCallback(() => {
        // Unconditionally clear (and null) any live interval — gating the clear
        // on props.task.running could leave one running while the tab is hidden.
        if (runningIntervalRef.current) {
            clearInterval(runningIntervalRef.current);
            runningIntervalRef.current = null;
        }
    }, []);

    /**
     * Called from the browser when the tab regains visibility
     */
    const reInitializeApp = useCallback(() => {
        // Only start one if we're running AND none is already live — otherwise a
        // visibility regain would orphan the interval already in the ref.
        if (props.task.running && !runningIntervalRef.current) {
            runningIntervalRef.current = setInterval(runningCallback, 1000);
        }
    }, [props.task.running, runningCallback]);

    const handleVisibilityChange = useCallback(() => {
        const {hidden} = util.getVisibilityInfo();

        if (document[hidden]) prepareForFreeze();
        else reInitializeApp();
    }, [prepareForFreeze, reInitializeApp]);

    useEffect(() => {
        // Get the visibility change listener
        const {visibilityChange} = util.getVisibilityInfo();
        document.addEventListener(visibilityChange, handleVisibilityChange, false);

        // Keep exactly one interval while running: start one only if we aren't
        // already running one, and stop a lingering one when we're not running.
        // Previously BOTH create and clear were gated on props.task.running, so
        // the false→true transition (the running prop flips a render after
        // onStartTimer seeds the interval) skipped the clear and orphaned it.
        if (props.task.running && !runningIntervalRef.current) {
            runningIntervalRef.current = setInterval(runningCallback, 1000);
        }
        if (!props.task.running && runningIntervalRef.current) {
            clearInterval(runningIntervalRef.current);
            runningIntervalRef.current = null;
        }

        // If we have the paused state set, and we get a new prop in saying that it is running, we need to override
        if ((props.task.running !== isRunning || props.task.completionDate !== completionDate)) {
            setIsRunning(props.task.running);
            setCompletionDate(props.task.completionDate);
            setAsOf(null);
        }

        return () => {
            // Remove page visibility change handler
            const {visibilityChange} = util.getVisibilityInfo();
            document.removeEventListener(visibilityChange, handleVisibilityChange);

            // Always clear — never leave an interval behind on unmount or re-run.
            if (runningIntervalRef.current) {
                clearInterval(runningIntervalRef.current);
                runningIntervalRef.current = null;
            }
        }
    }, [completionDate, handleVisibilityChange, isRunning, props.task.completionDate, props.task.running, runningCallback]);

    const onDragStart = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        if (props.onDragStart) props.onDragStart(e, props.task);
        else TaskHelper.onDragStart(e, props.task);
    }, [props]);

    const onTitleBlur: ChangeEventHandler<HTMLDivElement> = useCallback(e => {
        e.preventDefault();
        e.stopPropagation()

        if (props.onTitleBlur) props.onTitleBlur(props.task, e);
    }, [props]);

    const onTitleClick: MouseEventHandler<HTMLDivElement> = useCallback(e => {
        e.preventDefault();
        if (!props.clickPassThrough) e.stopPropagation();

        if (props.onTitleClick) props.onTitleClick(props.task, e.metaKey, e.shiftKey);
    }, [props]);

    const onTitleTouch: TouchEventHandler<HTMLDivElement> = useCallback(e => {
        e.preventDefault();
        e.stopPropagation()

        if (props.onTitleTouch) props.onTitleTouch(props.task);
    }, [props]);

    const onTitlePress: KeyboardEventHandler = useCallback(e => {
        e.preventDefault();
        e.stopPropagation()

        if (props.onTitlePress) props.onTitlePress(e.key, e.shiftKey, props.task);
    }, [props]);

    /**
     * Called when the timer is started.
     *
     * @param {Event} e
     * @private
     */
    const onStartTimer = useCallback(() => {
        if (!canStartTask(props.task, getCurrentUserId())) return;
        clearInterval(runningIntervalRef.current);
        runningIntervalRef.current= setInterval(runningCallback, 1000);

        // Set state variables
        setIsRunning(true);
        setAsOf(newHRTimestamp());

        // Stop the timer
        startTimer(props.task, getCurrentUserId());
    }, [props.task, runningCallback]);

    /**
     * Called when the timer is stopped.
     *
     * @param {Event} e
     * @private
     */
    const onPauseTimer = useCallback(() => {
        // Stop the one-second interval timer for this component
        clearInterval(runningIntervalRef.current);
        runningIntervalRef.current= null;

        // Set state variables
        setIsRunning(false);
        setAsOf(newHRTimestamp());

        // Stop the task itself
        stopTimer(props.task, getCurrentUserId());
    }, [props.task]);

    /**
     * Create the tooltip for the task
     *
     * @return {string}
     */
    const getTooltip = useCallback((): string => {
        const task = props.task;
        const title = task.title || AWAITING_TITLE;

        if (task.fatalError && (task.fatalError.message || task.fatalError.status)) {
            return i18n.cs('error') + (task.fatalError.message || `${task.fatalError.status}`);
        }
        else {
            let duration = '';
            if (task.duration) {
                if (task.duration < 1) duration = (60 * task.duration).toFixed(0) + ' minutes';
                else duration = (task.duration).toFixed(1) + ' hours';
            }

            const assignedToEntity = TaskHelper.getAssigned(task);
            const assignedTo = TaskHelper.getAssignedName(assignedToEntity);
            const owner = formatUserName(find('contacts', task.addedBy), {shorten: false});

            return [
                title,
                task.dueDate ? `Due on ${task.dueDate.toDateString()}` : '',
                task.duration ? `Requires ${duration}` : '',
                task.completionDate ? `Completed on ${task.completionDate.toDateString()}` : '',
                task.assignedTo ? `Assigned to ${assignedTo}` : '',
                `Owned (Added) By ${owner}`
            ]
                .filter(tt => tt !== '')
                .join('\n');
        }
    }, [props.task]);

    /**
     * The actual content of the task. Contained within a ContextMenu or as first div in a pair for mobile.
     *
     * We will show a Contact name if provided in RelatedTo, or else we will get the client name(s) from a related matter.
     */
    const getTaskContent = useCallback(() => {
        const task = props.task;

        try {
            const maxRatio = .5;

            const title = task.title || AWAITING_TITLE;

            let clientChars = 0;
            let maxChars = 0;

            const typeMap = new Map(); // Checked

            let userOrGroup: User | Group | null, contact: string;
            if (task.assignedTo) {
                userOrGroup = TaskHelper.getAssigned(task);
                if (userOrGroup) {
                    contact = TaskHelper.getAssignedName(userOrGroup, {shorten: true});
                    const nameLength = contact.length || 0;
                    clientChars = nameLength;
                    maxChars = nameLength;
                }
            }

            if (props.showDates) {
                clientChars += 6;
            }

            // Find clients in the relatedTo field
            const relatedTo = (props.showClientNames && !showActions && (task.relatedTo.length > 0) &&
                task.relatedTo.map(clientId => {
                        const formatted = formatUniqueNameOverride(
                            props.contacts,
                            clientId,
                            'LFM',
                            null,
                            props.unsavedEntity instanceof Contact ? props.unsavedEntity : null
                        );
                        clientChars += formatted.length;
                        maxChars = Math.max(maxChars, formatted.length);
                        typeMap.set(formatted, 'RelatedTo');
                        return formatted;
                    }
                ).flat()) || [];

            // Find clients related to this task's leads (if one is connected)
            const leadClients = (props.showClientNames && !showActions && (task.tieIns?.length > 0) &&
                task.tieIns
                    .filter(ti => ti.module === 'Sales' && ti.entity === 'Lead')
                    .map(ti => {
                        const lead = LeadHelper.getLeadFromId(ti.id);
                        if (!lead) return [''];
                        else return lead.clients.map(clientId => {
                            const formatted = formatUniqueNameOverride(
                                props.contacts,
                                clientId,
                                'LFM',
                                null,
                                props.unsavedEntity instanceof Contact ? props.unsavedEntity : null
                            );
                            clientChars += formatted.length;
                            maxChars = Math.max(maxChars, formatted.length);
                            typeMap.set(formatted, 'Sales');
                            return formatted;
                        });
                    }).flat()) || [];

            // Find clients related to this tasks' matter (if one is connected)
            const matterClients = (props.showClientNames && !showActions && (task.tieIns?.length > 0) &&
                task.tieIns
                    .filter(ti => ti.module === 'Client' && ti.entity === 'Matter')
                    .map(ti => {
                        const matter = CaseHelper.getMatterFromId(ti.id) as Matter;
                        if (!matter) return [''];
                        else
                            return matter.clients.map(clientId => {
                                const formatted = formatUniqueNameOverride(
                                    props.contacts,
                                    clientId,
                                    'LFM',
                                    null,
                                    props.unsavedEntity instanceof Contact ? props.unsavedEntity : null
                                );
                                clientChars += formatted.length;
                                maxChars = Math.max(maxChars, formatted.length);
                                typeMap.set(formatted, 'Client');
                                return formatted;
                            });
                    }).flat()) || [];

            // Combine them
            let clientNames = matterClients.concat(leadClients);
            if (showRelatedTo) clientNames = clientNames.concat(relatedTo);

            let gridTemplateColumns = '';
            const style: CSSProperties = {};

            let leftContainer: any, rightContainer: any;

            // In cases where the title is null (not yet set), use a length of 1 for the denominator
            const clientToTextRatio = (clientChars + clientNames.length) / (title.length);

            let frCount = Math.min(
                clientNames.length,
                Math.max(
                    isTouchDevice() ? 1 : (clientToTextRatio < maxRatio ? 1 : 2),
                    clientNames.length > 0 ? Math.floor(1 + clientToTextRatio) : 1, // Minimum of 1
                )
            );

            // If we are passed a width in props, base gridTemplateColumns on width
            if (props.width) {
                const boxWidth = props.width * .75 * .33;
                const charWidth = 10 * maxChars;

                // We don't want three columns and four values, makes no sense
                const newFrCount = Math.max(Math.floor(boxWidth / Math.max(1, charWidth)), 1);
                if (frCount % newFrCount === 0) frCount = newFrCount;

                if (frCount === 1 && (charWidth + title.length > boxWidth)) {
                    style.maxWidth = '10em';
                }
            }

            for (let i = 0; i < frCount; i++) {
                gridTemplateColumns += 'auto ';
            }

            const clientNameElements = clientNames
                .filter(f => !!f)
                .filter(util.distinctFilter)
                .map(formatted => {
                    return <div key={formatted}
                                className={`${typeMap.get(formatted)} Tag`}
                                style={style}
                    >
                        {formatted}
                    </div>;
                });

            const hasElements = clientNameElements.length > 0;
            typeMap.clear();

            if (isTouchDevice()) {
                leftContainer = props.useOneLine || (TaskHelper.amIAssigned(task) && hasElements) ? 'u-row' : 'u-column';
                rightContainer = leftContainer;
            }
            else {
                leftContainer = props.useOneLine || (TaskHelper.amIAssigned(task) || hasElements || props.expandCell || !(hasElements && frCount === 1)) ? 'u-row' : 'u-column';
                rightContainer = (props.expandCell || (clientToTextRatio < .5 && leftContainer === 'u-row')) ? 'u-row' : 'GridLayout';
                if (title.length > 40 && !props.expandCell && !props.useOneLine) leftContainer = 'u-column';
            }

            function getDueDateFormat(task: Task) {
                const dt = task.completionDate || task.dueDate;

                if (!dt) return 'MM/DD/YY';

                // Get the current year
                const currentYear = Moment().get('year');
                const taskDate = DateUtil.toMoment(dt);
                const taskYear = taskDate.get('year');
                if (currentYear === taskYear) return taskDate.format('MM/DD');
                else return taskDate.format('MM/DD/YY');
            }

            const dueDate = props.showDates && !showActions && (
                <div className='Date Tag'>
                    {getDueDateFormat(task)}
                </div>
            );

            const assignee = TaskHelper.getAssignedName(TaskHelper.getAssigned(task), {shorten: true});
            const assignedToDiv = showOwner && (task.assignedTo !== getCurrentUserId()) && (
                <div className="AssignedTo Tag">{assignee}</div>
            );

            const onclick: MouseEventHandler<HTMLDivElement> = !window.__is_mobile__ ? onTitleClick : undefined;
            const ontouchend: TouchEventHandler<HTMLDivElement> = window.__is_mobile__ ? onTitleTouch : undefined;

            const hasRight = clientNames && clientNames.length > 0;
            const tagContainer = <>
                <div className="TagContainer">
                    <div className={`Tags CurvedLeft ${leftContainer} ${!hasRight ? 'Only' : ''}`}>
                        {dueDate}
                        {assignedToDiv}
                    </div>

                    {hasRight &&
						<div
							className={`Tags CurvedRight ${rightContainer}`}
							style={rightContainer === 'GridLayout' && !isTouchDevice() ? {
                                gridTemplateColumns: gridTemplateColumns
                            } : {}}
						>
                            {...clientNameElements}
						</div>
                    }
                </div>

                <StateLabel item={task} title={getTooltip()}>
                    <div className={`Text ${task.status === 'D' ? 'u-deleted' : ''}`.trim()}
                         onBlur={onTitleBlur}
                         onKeyDown={onTitlePress}
                         contentEditable={props.editTitle}
                         suppressContentEditableWarning={props.editTitle}
                         dangerouslySetInnerHTML={TextField.createMarkup(window.__demo_mode__
                                                                         ? replaceNames(props.contacts.map(c => c.id), title) as string
                                                                         : title)}
                    />
                </StateLabel>
            </>;

            if ((!window.__is_mobile__ && onclick) || (window.__is_mobile__ && ontouchend))
                return <div className="Title"
                            onClick={onclick}
                            onTouchEnd={ontouchend}
                            draggable={!props.editTitle && !!props.onDragStart}
                            onDragStart={onDragStart}
                >
                    {tagContainer}
                </div>;
            else return (
                <div className="Title">{tagContainer}</div>
            );
        }
        catch (error) {
            console.error(error);
            return iconErrorRender(error);
        }
    }, [props.showDates, props.showClientNames, showActions, showRelatedTo, props.width, props.useOneLine, props.expandCell, showOwner, onTitleClick, onTitleTouch, onTitleBlur, onTitlePress, props.editTitle, props.onDragStart, onDragStart, props.task, props.contacts, props.unsavedEntity, getTooltip]);

    const fnShowActions = () => {
        setShowActions(false);
        setTimeout(() => {
            props.onMenuClicked?.(props.task);
            props.onEndEdit?.();
        }, 100);
    };

    const onShowActions = (e: SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();

        props.onBeforeEdit?.();

        dialogNoButtons('Task Actions',
            <DivMenu onSelection={fnShowActions}
                     items={contextMenuItems}
            />, {}
        )
    };

    const onCheckboxClick = (e: SyntheticEvent) => {
        e.preventDefault();

        setAsOf(newHRTimestamp());
        setCompletionDate(props.task.completionDate ? null : new Date());

        setTimeout(() => {
                const onClick = props.onCheckboxClick || util.noop;
                onClick(props.task);
            },
            100
        );
    };

    const renderPausedTimer = () => {
        const t = props.task;
        if (!t) return null;

        const elapsedHours = t.elapsedTime ? t.elapsedTime / 3600000 : null; // elapsed time in hours
        const color = elapsedHours > 0 ? 'green' : t.duration ? 'black' : 'maroon';

        const formattedTime = formatDuration(t.elapsedTime, 'hh:mm:ss');

        return <span className={`Task__paused-timer Task__${color}`}>{formattedTime}</span>;
    }

    const renderTimer = () => {
        if (!props.task) return null;
        const ms = (props.task.elapsedTime + Math.max(0, Date.now() - props.task.lastStart));
        return formatDuration(ms, 'hh:mm:ss');
    };

    const calculateBackground = () => {
        if (!props.task) return '';
        return getTaskBackgroundColor(task);
    }


    const style: CSSProperties = {};

    const task = props.task;
    if (!task) return null;

    const title = task.title || AWAITING_TITLE;
    const selected = (task.id === props.selectedTaskId) || props.isSelected;

    // Set the title color to blue for persisting, or green for running
    let className = '';
    const isItRunning = asOf ? isRunning : task.running;
    if (isItRunning) className = 'Running';

    const _completionDate = asOf && completionDate !== task.completionDate
                            ? completionDate
                            : task.completionDate;

    const isPlayable = TaskHelper.canPlay(task);

    const billableTimeCheck = task.completionDate ? true : task.billable ? task.elapsedTime > 0 : true;

    const iAmFrom = task.notification?.from === getCurrentUserId();
    const iAmTo = task.notification?.to === getCurrentUserId();
    const status = task.notification?.status;

    const notificationType = status === 'r' && iAmTo ? 'NotificationRed' : status === 'w' && iAmFrom ? 'NotificationOrange' : '';

    const mouseEnter = props.onMouseEnter || util.noop;
    const mouseLeave = props.onMouseLeave || util.noop;

    const docs = props.documents.getMappingFor('entityId', task.id).filter(deletedFilter);

    if (props.calculateBackgrounds) {
        style.backgroundColor = calculateBackground();
    }

    return (
        <InView>
            {({inView, ref}) => {
                return <div id={task.id}
                            ref={ref}
                            style={{...style}}
                            className={[
                                'Task',
                                className,
                                selected ? 'Task--selected' : '',
                                notificationType,
                            ].join(' ').trim()}
                            onMouseEnter={wrapHandler(inView, () => mouseEnter(task))}
                            onMouseLeave={wrapHandler(inView, () => mouseLeave(task))}
                >
                    {inView && !!task.completionDate && !showActions && props.useCompletionMark && (
                        <IconButton id={`COMPLETED__${title}`}
                                    type={Icons.CHECKED}
                                    title='Completed'
                        />
                    )}

                    {/* This is the checkmark next to each task */}
                    {inView && !showActions && props.showCheckbox && !!task.elapsedTime ?
                     <IconButton
                         id={`CHECK__${title}`}
                         type={_completionDate !== null ? Icons.CHECKED : Icons.UNCHECKED}
                         size={Icons.IconSizeNormal}
                         title={!billableTimeCheck ? 'No billable time added' : (_completionDate !== null) ? 'Mark not finished' : 'Mark finished'}
                         onClick={wrapHandler(inView, onCheckboxClick)}
                         disabled={!billableTimeCheck || !props.onCheckboxClick}
                     /> : null
                    }

                    {/* This is the checkmark next to each task */}
                    {inView && props.anyPlayable
                     ? isPlayable && !_completionDate && !showActions && props.showTimer
                       ? <IconButton
                           id={`PLAY__${title}`}
                           type={isItRunning ? Icons.PAUSE : Icons.PLAY}
                           size={Icons.IconSizeNormal}
                           color={isItRunning ? 'red' : 'green'}
                        disabled={!isItRunning && !canStartTask(task, getCurrentUserId())}
                        title={isItRunning ? 'Pause Timer' : !canStartTask(task, getCurrentUserId()) ? TASK_ASSIGN_BEFORE_START : 'Start Timer'}
                           onClick={isItRunning
                                    ? wrapHandler(inView, onPauseTimer)
                                    : wrapHandler(inView, onStartTimer)}
                       />
                       : null //<IconButton type={Icons.BLANK}/>
                     : null
                    }

                    {/* If a timer is running, show the clock */}
                    {task.running && !showActions && props.showElapsedTime &&
						<div className='Timer'>{renderTimer()}</div>
                    }

                    {!isItRunning && props.showElapsedTime && !!task.elapsedTime ?
                     <div className='Timer'>{renderPausedTimer()}</div> : null}

                    {/* If in a content block, offset by the content block's height */}
                    {inView ? <ContextMenu items={contextMenuItems}
                                           yOffset={props.inSmallBlock ? 20 * 15 : 0}
                    >
                        {getTaskContent()}
                    </ContextMenu> : getTaskContent()
                    }

                    {task.status === 'D' && !showActions && !isMobileView() && (
                        <IconButton id={`DELETED__${title}`}
                                    type={Icons.SEARCH_CANCEL}
                                    size={Icons.IconSizeSmall}
                                    color='maroon'
                                    title='Deleted'
                        />
                    )}

                    {/* Emojis */}
                    {OptionHelper.getOptionValueStatic(OptionHelper.TASK_EMOJIS) && showEmojiBar &&
						<EmojiBar onChange={util.noop}
						          values={task.emojis}
						          smallView={true}
						/>
                    }

                    {task.private && !showActions && !isMobileView() && (
                        <IconButton id={`PRIVATE__${title}`}
                                    type={Icons.PRIVATE}
                                    size={Icons.IconSizeSmall}
                                    color='rgba(255,0,0,.6)'
                                    title='Private'
                        />
                    )}

                    {task.billable && !showActions && !isMobileView() && (
                        <IconButton id={`BILLABLE__${title}`}
                                    type={Icons.DOLLARSIGN}
                                    size={Icons.IconSizeSmall}
                                    color='green'
                                    title='Billable'
                        />
                    )}

                    {docs.length > 0 && (
                        <IconButton id={`ATTACHMENTS__${title}`}
                                    type={Icons.FILES}
                                    size={Icons.IconSizeSmall}
                                    count={docs.length}
                                    countBackground='#1c2341'
                                    color='#1c2341'
                                    title='Has attachments'
                        />
                    )}

                    {task.invoiceId && FinancialHelper.getFinalizedByInvoiceId(task.invoiceId) && !showActions && !isMobileView() && (
                        <IconButton id={`FINALIZED__${title}`}
                                    type={Icons.LOCK}
                                    size={Icons.IconSizeSmall}
                                    color='maroon'
                                    title='Part of finalized invoice'
                        />
                    )}

                    {/* Notifications */}
                    {task.notification.status && (
                        <NotificationFlag notification={task.notification}
                                          target={task.assignedTo || task.createdBy}
                                          flagOnly={true}
                                          isGroup={task.assignedToType === 'G'}
                        />
                    )}

                    {/* Context Menu Button, if mobile */}
                    {isTouchDevice() && !showActions && !props.hideActionsOnMobile &&
						<IconButton id={`ELLIPSES_${title}`}
						            className="TaskButtons"
						            type={Icons.ELLIPSES}
						            onClick={wrapHandler(inView, (e: SyntheticEvent) => onShowActions(e))}
						            clickPassthrough={true}
						/>
                    }
                </div>
            }}
        </InView>
    );
}

export type TaskComponentProps = Partial<RootState> & {
    task: Task,
    clickPassThrough: boolean,
    anyPlayable?: boolean,
    contextMenuItems: ContextMenuItemProps[],
    editTitle?: boolean,
    expandCell?: { impact: ImpactType, urgency: UrgencyType },
    hideActionsOnMobile?: boolean, // Force hiding of the ellipses actions in mobile
    inSmallBlock?: boolean,  // Need to know if we are in an unexpanded content block.
    isSelected?: boolean,
    onEndEdit?: VoidCallback,
    onBeforeEdit?: VoidCallback,
    onCheckboxClick?: (task: Task) => void,
    onDragStart?: (event: React.DragEvent<HTMLSpanElement>, task: Task) => void,
    onMenuClicked?: (task: Task) => void, // Callback for when context menu clicked on mobile
    onMouseEnter?: (task: Task) => void,
    onMouseLeave?: (task: Task) => void,
    onSelected?: (task: Task, shouldSelect: boolean) => void,
    onTitleBlur?: (task: Task, e: ChangeEvent<HTMLDivElement>) => void,
    onTitleClick?: (task: Task, isMeta: boolean, isShift: boolean) => void,
    onTitlePress?: (key: string, shift: boolean, task: Task) => void,
    onTitleTouch?: (task: Task) => void,
    selectedTaskId?: string,
    showClientNames?: boolean,
    showDates?: boolean,
    showDocket?: boolean,
    showCheckbox?: boolean,
    showElapsedTime?: boolean,
    showEmojiBar?: boolean,
    showOwner?: boolean,
    showRelatedTo?: boolean,
    showTimer?: boolean,
    unsavedEntity?: EntityType,
    calculateBackgrounds?: boolean,
    width?: number,

    useCompletionMark?: boolean, // Should we use an icon to indicate that this task has been completed?

    // Use one line for date, assignedTo, etc
    useOneLine?: boolean,
};

const mapStateToProps = (state: RootState) => {
    return {
        contacts: state.contacts,
        documents: state.documents
    }
}

export default connect(mapStateToProps)(TaskComponent);
